<script setup lang="ts">
import { ref, reactive, nextTick } from 'vue'
import { NButton, NFormItem, NInput, NSelect, useMessage } from 'naive-ui'
import ReminderScheduleRow from './ReminderScheduleRow.vue'
import { useRemindersStore, type ReminderScheduleInput } from '../stores/reminders'

// The recurring-schedule half of the Reminders card (#1092) — the add/edit
// composer and the list of recurring schedules.

const store = useRemindersStore()
const message = useMessage()

const DEFAULT_FIRE_TIME = '09:00'

const scheduleMessageRef = ref<InstanceType<typeof NInput> | null>(null)

const scheduleForm = reactive<ReminderScheduleInput>({
  message: '',
  recurrence_type: 'daily',
  fire_at_time: DEFAULT_FIRE_TIME,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  ends_on: null,
  day_of_week: null,
})
const editingScheduleId = ref<number | null>(null)
const savingSchedule = ref(false)

const INPUT_CLASS =
  'min-h-10 w-full rounded-[10px] border border-border-control bg-surface-raised px-3 text-sm text-ink [color-scheme:dark]'

const RECURRENCE_OPTIONS = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
]

const DAY_OF_WEEK_OPTIONS = [
  { label: 'Sunday', value: 0 },
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
]

async function focusForm(): Promise<void> {
  await nextTick()
  scheduleMessageRef.value?.focus()
}

async function startEditSchedule(id: number) {
  const s = store.schedules.find((schedule) => schedule.id === id)
  if (!s) return
  Object.assign(scheduleForm, {
    message: s.message,
    recurrence_type: s.recurrence_type,
    fire_at_time: s.fire_at_time,
    timezone: s.timezone,
    ends_on: s.ends_on ?? null,
    day_of_week: s.day_of_week ?? null,
  })
  editingScheduleId.value = id
  await focusForm()
}

function cancelEditSchedule() {
  editingScheduleId.value = null
  Object.assign(scheduleForm, {
    message: '',
    recurrence_type: 'daily',
    fire_at_time: DEFAULT_FIRE_TIME,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ends_on: null,
    day_of_week: null,
  })
}

async function saveSchedule() {
  if (!scheduleForm.message.trim()) {
    message.error('Message is required')
    return
  }
  if (!scheduleForm.fire_at_time) {
    message.error('Time is required')
    return
  }
  savingSchedule.value = true
  try {
    if (editingScheduleId.value !== null) {
      await store.updateSchedule(editingScheduleId.value, { ...scheduleForm })
      message.success('Schedule updated')
    } else {
      await store.createSchedule({ ...scheduleForm })
      message.success('Schedule created')
    }
    cancelEditSchedule()
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save schedule')
  } finally {
    savingSchedule.value = false
  }
}

async function deleteSchedule(id: number) {
  try {
    await store.deleteSchedule(id)
    message.success('Schedule deleted')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to delete schedule')
  }
}
defineExpose({ focusForm })
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Add / edit schedule composer -->
    <div
      role="group"
      class="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-bg p-3"
      :aria-label="editingScheduleId !== null ? 'Edit schedule' : 'Add recurring schedule'"
    >
      <p class="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">
        {{ editingScheduleId !== null ? 'Edit schedule' : 'Add recurring schedule' }}
      </p>
      <NInput
        ref="scheduleMessageRef"
        v-model:value="scheduleForm.message"
        placeholder="e.g. Weekly standup review"
        :input-props="{ 'aria-label': 'Schedule message' }"
        @keydown.enter.prevent="saveSchedule"
      />
      <div class="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
        <NFormItem label="Recurrence" :show-feedback="false">
          <NSelect
            v-model:value="scheduleForm.recurrence_type"
            :options="RECURRENCE_OPTIONS"
            @update:value="
              (v) => {
                if (v !== 'weekly') scheduleForm.day_of_week = null
              }
            "
          />
        </NFormItem>
        <NFormItem label="Time" :show-feedback="false">
          <input v-model="scheduleForm.fire_at_time" type="time" aria-label="Fire time (HH:MM)" :class="INPUT_CLASS" />
        </NFormItem>
        <NFormItem v-if="scheduleForm.recurrence_type === 'weekly'" label="Day of week" :show-feedback="false">
          <NSelect
            v-model:value="scheduleForm.day_of_week"
            :options="DAY_OF_WEEK_OPTIONS"
            clearable
            placeholder="Any day (rolling 7 days)"
          />
        </NFormItem>
        <NFormItem label="End date (optional)" :show-feedback="false">
          <input
            v-model="scheduleForm.ends_on"
            type="date"
            aria-label="End date (optional, leave blank for indefinite)"
            :class="INPUT_CLASS"
          />
        </NFormItem>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <NButton type="primary" :loading="savingSchedule" @click="saveSchedule">
          {{ editingScheduleId !== null ? 'Save changes' : 'Create schedule' }}
        </NButton>
        <NButton v-if="editingScheduleId !== null" quaternary @click="cancelEditSchedule">Cancel</NButton>
        <span class="ml-auto font-mono text-xs text-ink-faint">{{ scheduleForm.timezone }}</span>
      </div>
    </div>

    <p v-if="store.schedules.length === 0" class="m-0 py-6 text-center text-ink-muted">No recurring schedules yet</p>
    <div v-else class="pt-2">
      <h3 class="m-0 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">Recurring</h3>
      <ReminderScheduleRow
        v-for="s in store.schedules"
        :key="s.id"
        :schedule="s"
        @edit="startEditSchedule"
        @delete="deleteSchedule"
      />
    </div>
  </div>
</template>
